import { computed, DestroyRef, inject, Injectable } from '@angular/core';
import { FiLikesFactoryService } from '@fi-shared/fi-likes/services/fi-likes-factory.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { IReactionData, IReactionFavoriteModel } from '../fi-likes';
import { FiEmojiService } from '@fi-shared/fi-emoji-button/fi-emoji.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { isDefined } from '@fi-shared/utils';
import { FiSignalRService } from '@fi-shared/fi-signal-r/fi-signal-r.service';
import { getClientGuid } from '@fi-shared/utils/client-guid';

@Injectable()
export class FiLikesStoreService {
    private fiLikesFactoryService = inject(FiLikesFactoryService);
    private userDataService = inject(FiUserDataService);
    private fiEmojiService = inject(FiEmojiService);
    private fiSignalRService = inject(FiSignalRService);

    private readonly MaxReactionFavoritesCount = 5;

    init(destroyRef: DestroyRef) {
        this.fiSignalRService.reactionFavoriteUpdated.subscribeWithDestroyRef((data) => {
            if (data.ClientGuid === getClientGuid()) {
                // don't do anything if current user made changes
                return;
            }
            this.updateReactionFavorites(data.ReactionFavorite.Data);
        }, destroyRef);
    }

    favoriteReactions = computed<IReactionFavoriteModel[]>(() => {
        const favoriteReactions = this.userDataService.favoriteReactions() ?? [];
        if (favoriteReactions.length < this.MaxReactionFavoritesCount) {
            // here we should add default favorite reactions to user's favorite reactions if there are less than MaxReactionFavoritesCount
            const diffCount = this.MaxReactionFavoritesCount - favoriteReactions.length;
            const existingUnifies = favoriteReactions.map((fr) => fr.Data.Unified);
            const defaultFavoriteReactions = this.fiEmojiService.defaultEmojies
                .filter((emoji) => !existingUnifies.includes(emoji.unified))
                .map((emoji) =>
                    this.fiLikesFactoryService.getFavoriteReactionInstance(
                        new Date(),
                        this.fiLikesFactoryService.getReactionDataFromEmoji(emoji),
                    ),
                )
                .slice(0, diffCount);
            favoriteReactions.push(...defaultFavoriteReactions);
        }
        return favoriteReactions;
    });

    favoriteEmojiUnifiedCodes = computed<string[]>(() => {
        const favoriteReactions = this.favoriteReactions();
        const unifiedCodes = favoriteReactions.map((r) => r.Data.Unified);
        return unifiedCodes;
    });

    updateReactionFavorites(reactionData: IReactionData): void {
        const favoriteReactions = [...this.userDataService.favoriteReactions()];
        const currentReactionFavoriteIdx = favoriteReactions.findIndex((r) =>
            this.fiLikesFactoryService.isReactionDataEquals(r.Data, reactionData),
        );
        if (currentReactionFavoriteIdx !== -1) {
            //delete existing favorite reaction
            favoriteReactions.splice(currentReactionFavoriteIdx, 1);
        }
        const newFavoriteReaction = this.fiLikesFactoryService.getFavoriteReactionInstance(new Date(), reactionData);
        favoriteReactions.unshift(newFavoriteReaction);
        if (favoriteReactions.length > this.MaxReactionFavoritesCount) {
            favoriteReactions.splice(this.MaxReactionFavoritesCount);
        }
        this.userDataService.updateFavoriteReactions(favoriteReactions);
    }
}
